import { useState } from 'react';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import Link from '@activebrands/core-web/components/Link';
import withHeightAuto from '@activebrands/core-web/components/withHeightAuto';
import media from '@activebrands/core-web/config/media';
import LipScoreServiceSmallReviewsBadge from '@activebrands/core-web/libs/lipscore/serviceReviewWidgets/LipScoreServiceSmallReviewsBadge';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import transformPreviewEditable from '@activebrands/core-web/libs/storyblok/utils/transform-preview-editable';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import ArrowButton from 'components/buttons/ArrowButton';
import JohaugIcon from 'components/icons/JohaugIcon';
import StateIcon from 'components/icons/StateIcon';
import Paragraph from 'components/text/Paragraph';
import fm from 'format-message';

const Wrapper = styled('div', {
    width: '100%',
    padding: '0 20px',
    backgroundColor: 'var(--color-contrast-lowest)',
    borderRadius: '24px 24px 0px 0px',
    borderTop: '0.5px solid var(--color-contrast-lower)',

    [media.min['tablet.sm']]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '8px',
        borderRadius: '16px 16px 0px 0px',
    },

    [media.min['desktop.sm']]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '8px',
        borderRadius: '16px 16px 0px 0px',
    },
});

const LogoAndReviewsWrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    alignItems: 'center',
    padding: '16px 0 48px 0',
    position: 'relative',
    width: '100%',

    [media.min['tablet.sm']]: {
        gridColumn: '1 / span 4',
        gridRow: 1,
        alignItems: 'flex-start',
        padding: '32px 0 100px 0',
    },
});

const AccordionWrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '48px',

    [media.min['tablet.sm']]: {
        gridColumn: '6 / span 6',
        gridRow: 2,
        flexDirection: 'row',
        marginBottom: '0',
    },
});

const FeaturedWrapper = styled('div', {
    padding: '16px 0 48px 0',

    [media.min['tablet.sm']]: {
        padding: '32px 0 48px 0',
        gridColumn: '6 / span 7',
        gridRow: 1,
    },
});

const FooterNavigation = styled('nav', {
    display: 'contents',
});

const FeaturedLinksWrapper = styled('ul', {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    listStyle: 'none',
    margin: 0,
    padding: 0,
});

const MenuItem = styled('div', {
    [media.min['tablet.sm']]: {
        wordWrap: 'break-word',
        width: '33.33%',
    },
});

const MenuHeaderMobile = styled('button', {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: '8px',
    padding: '8px 16px 10px 16px',
    backgroundColor: 'var(--color-bg-footer-accordion)',
    borderRadius: '100px',
    height: '42px',
    lineHeight: '100%',

    [media.min['tablet.sm']]: {
        display: 'none',
    },
    ...getFocusRingStyles(),
});

const MenuHeader = styled('button', {
    display: 'none',

    [media.min['tablet.sm']]: {
        visibility: 'visible',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 8px 5px 8px',
        backgroundColor: 'unset',
        width: 'unset',
        height: 'unset',
        borderRadius: '100px',
        lineHeight: '100%',
        marginBottom: '16px',
        cursor: 'auto',
        border: '1px solid',
    },
});

const Content = withHeightAuto(
    styled('div', {
        overflow: 'hidden',
        transitionDuration: '500ms',
        transitionProperty: 'height',
        transitionTimingFunction: 'var(--ease)',
        backgroundColor: 'var(--color-border-button-tertiary)',
        borderRadius: '0 0 15px 15px',

        [media.min['tablet.sm']]: {
            height: 'auto !important',
            overflow: 'visible',
            transition: 'none',
            color: 'var(--color-text)',
        },
    })
);
const InnerContent = styled('ul', {
    padding: '4px 8px',
    listStyle: 'none',
    margin: 0,

    [media.min['tablet.sm']]: {
        padding: 0,
    },
});

const StyledLink = styled(Link, {
    display: 'block',
    marginBottom: '16px',
    ...getFocusRingStyles(),
});

const FooterMenuInfo = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '48px',
    gap: '16px',
    ...textStyles['Secondary/14_130'],

    [media.min['tablet.sm']]: {
        paddingBottom: '176px',
    },
});

const isInternalLink = url => typeof url === 'string' && /^[/?#](?!\/)/.test(url);

const getFooterLinkAriaLabel = (label, url, target) => {
    if (target === '_blank') {
        return `${label} ${fm('(opens in a new window)')}`;
    }
    if (url && !isInternalLink(url)) {
        return `${label} ${fm('(external website)')}`;
    }
    return undefined;
};

const FooterMenu = ({ items, informationHeading, featuredLinks, featuredLinksLabel, informationParagraphs }) => {
    const [css] = useStyletron();
    const [openStates, setOpenStates] = useState({});
    const [iconStates, setIconStates] = useState({});
    const onClickUsercentrics = () => {
        window.__ucCmp?.showSecondLayer();
    };
    return (
        <Wrapper>
            <LogoAndReviewsWrapper>
                <JohaugIcon
                    data-selector="global.footer.logo"
                    $style={{ maxWidth: ['160px', null, null, '232px', null, '300px'] }}
                    width="auto"
                />
                <div
                    className={css({ marginTop: ['32px', null, null, '24px'] })}
                    data-selector="global.footer.brandstory.badge"
                >
                    <LipScoreServiceSmallReviewsBadge type="long" />
                </div>
            </LogoAndReviewsWrapper>
            <FooterNavigation aria-label={fm('Footer menu')}>
                <FeaturedWrapper>
                    <div
                        data-selector="global.footer.quicklinks.title"
                        className={css({
                            marginBottom: ['16px', null, null, '24px'],
                            fontKeys: 'Primary/20-120_-1',
                            fontSize: '20px',
                        })}
                    >
                        {featuredLinksLabel}
                    </div>
                    <FeaturedLinksWrapper>
                        {featuredLinks?.map((link, linkIndex) => {
                            const featuredUrl = link?.link?.url;
                            const featuredTarget = link?.link?.target;

                            if (!featuredUrl) {
                                return null;
                            }

                            return (
                                <li key={`${link.label}-${linkIndex}`}>
                                    <ArrowButton
                                        $style={{ textDecoration: 'none', margin: '0px 4px 8px 0px' }}
                                        as={Link}
                                        fontKeys={['Miscellaneous/14_100', null, null, 'Miscellaneous/24_100_-2']}
                                        theme="default"
                                        to={featuredUrl}
                                        data-selector="global.footer.quicklink"
                                        target={featuredTarget}
                                        aria-label={getFooterLinkAriaLabel(link?.label, featuredUrl, featuredTarget)}
                                        {...transformPreviewEditable(link._editable)}
                                    >
                                        {link.label}
                                    </ArrowButton>
                                </li>
                            );
                        })}
                    </FeaturedLinksWrapper>
                </FeaturedWrapper>
                <AccordionWrapper data-selector="global.footer.linkcols">
                    {items.map((footerItem, footerItemIndex) => {
                        const open = openStates[footerItemIndex] || false;
                        const iconState = iconStates[footerItemIndex] || 'plus';
                        const sbEditable = transformPreviewEditable(footerItem._editable);

                        return (
                            <MenuItem
                                key={`${footerItem.label}-${footerItemIndex}`}
                                data-selector="global.footer.linkcol"
                                {...sbEditable}
                            >
                                <MenuHeaderMobile
                                    data-selector="global.footer.linkcol.title"
                                    type="button"
                                    aria-expanded={open}
                                    aria-controls={`footer-menu-content-${footerItemIndex}`}
                                    onClick={() => {
                                        const newOpenState = !open;
                                        const newIconState = iconState === 'minus' ? 'plus' : 'minus';

                                        setOpenStates(prev => ({
                                            ...prev,
                                            [footerItemIndex]: newOpenState,
                                        }));

                                        setIconStates(prev => ({
                                            ...prev,
                                            [footerItemIndex]: newIconState,
                                        }));
                                    }}
                                >
                                    <Paragraph fontKeys={['Miscellaneous/24_100_-2']}>{footerItem.label}</Paragraph>
                                    <StateIcon
                                        $style={{ display: [null, null, null, 'none'] }}
                                        size="16px"
                                        state={iconState}
                                    />
                                </MenuHeaderMobile>
                                <MenuHeader data-selector="global.footer.linkcol.title" tabIndex={-1}>
                                    <Paragraph fontKeys={['Miscellaneous/16_100_-1']}>{footerItem.label}</Paragraph>
                                </MenuHeader>
                                <Content id={`footer-menu-content-${footerItemIndex}`} height={open ? 'auto' : 0}>
                                    <InnerContent>
                                        {footerItem?.menuItems?.map((footerMenuItem, footerMenuItemIndex) => {
                                            const menuItemUrl = footerMenuItem.link?.url;
                                            const menuItemTarget = footerMenuItem.link?.target;

                                            if (!menuItemUrl) {
                                                return null;
                                            }

                                            return (
                                                <li
                                                    key={`${footerMenuItem.label}-${footerItemIndex}-${footerMenuItemIndex}`}
                                                >
                                                    <StyledLink
                                                        rel={
                                                            footerMenuItem.follow === 'no_follow'
                                                                ? 'nofollow'
                                                                : undefined
                                                        }
                                                        to={menuItemUrl}
                                                        data-selector="global.footer.link"
                                                        target={menuItemTarget}
                                                        aria-label={getFooterLinkAriaLabel(
                                                            footerMenuItem.label,
                                                            menuItemUrl,
                                                            menuItemTarget
                                                        )}
                                                        {...transformPreviewEditable(footerMenuItem._editable)}
                                                    >
                                                        {footerMenuItem.label}
                                                    </StyledLink>
                                                </li>
                                            );
                                        })}
                                        {
                                            // Usercentrics cookie settings link
                                            footerItem?.cookieLinkLabel && (
                                                <li>
                                                    <StyledLink
                                                        isVolatile
                                                        data-selector="global.footer.link"
                                                        className={css({ pointer: 'cursor' })}
                                                        aria-label={footerItem.cookieLinkLabel}
                                                        onClick={onClickUsercentrics}
                                                    >
                                                        {footerItem.cookieLinkLabel}
                                                    </StyledLink>
                                                </li>
                                            )
                                        }
                                    </InnerContent>
                                </Content>
                            </MenuItem>
                        );
                    })}
                </AccordionWrapper>
            </FooterNavigation>
            <div className={css({ gridColumn: '1 / span 4' })} data-selector="global.footer.brandstory">
                {informationHeading?.text && (
                    <div
                        style={{ marginBottom: '16px', fontKeys: 'Primary/20-120_-1', fontSize: '20px' }}
                        data-selector="global.footer.brandstory.title"
                        {...informationHeading.sbEditable}
                    >
                        {informationHeading.text}
                    </div>
                )}
                {informationParagraphs?.length > 0 && (
                    <FooterMenuInfo data-selector="global.footer.brandstory.body">
                        {informationParagraphs.map(paragraph => (
                            <div key={paragraph._uid} {...paragraph.sbEditable}>
                                <RichText data={paragraph.text} />
                            </div>
                        ))}
                    </FooterMenuInfo>
                )}
            </div>
        </Wrapper>
    );
};

FooterMenu.propTypes = {
    featuredLinks: PropTypes.array,
    featuredLinksLabel: PropTypes.string,
    informationHeading: PropTypes.object,
    informationParagraphs: PropTypes.arrayOf(PropTypes.object),
    items: PropTypes.array,
};

export default FooterMenu;
